import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';
import { ReactNode } from 'react';

import { Bem } from '../../service/Bem/Bem';
import {
    formatNumber,
    parseNumber
} from '../../service/FormatHelpers/FormatHelpers';
import { validatePhraseBid } from '../../service/PhraseMoneyCell/PhraseMoneyCell';
import { CurrencyLimitData } from '../../typings/constants';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { ErrorItemText } from '../ErrorsPresenter/ErrorsPresenter';
import { PhrasePriceEditor } from '../PhrasePriceEditor/PhrasePriceEditor';
import { PopupDecorator } from '../PopupDecorator/PopupDecorator';

import './PhrasePricePopupEditor.css';
import * as keyset from './PhrasePricePopupEditor.i18n';

const i18n = i18nFactory(keyset);

interface PhrasePricePopupEditorProps {
    price?: number;
    currency: string;
    onSave: (value: number) => void;
    anchor: ReactNode;
    isSaving: boolean;
    isPopupOpen: boolean;
    onCancel: () => void;
    error?: string;
    priceLimit: CurrencyLimitData;
}

interface PhrasePricePopupEditorState {
    text: string;
}

export class PhrasePricePopupEditor extends React.Component<
    PhrasePricePopupEditorProps,
    PhrasePricePopupEditorState
> {
    constructor(props: PhrasePricePopupEditorProps) {
        super(props);

        this.state = {
            text: props.price ? formatNumber(props.price, 2) : ''
        };
    }

    componentDidUpdate(prevProps: PhrasePricePopupEditorProps) {
        // Если в результате сохранения изменялась цена и нет ошибок, закрываем попап
        if (
            !this.props.error &&
            !this.props.isSaving &&
            prevProps.price !== this.props.price
        ) {
            this.cancel();
        }
    }

    render() {
        const { isPopupOpen, anchor } = this.props;

        return (
            <PopupDecorator
                onOutsideClick={this.cancel}
                anchor={anchor}
                visible={isPopupOpen}
                target="anchor"
                directions={[
                    'bottom-center',
                    'top-center',
                    'left-center',
                    'right-center'
                ]}
                footer={this.getFooterContent()}
                hasTail={true}
            >
                {this.getPopupContent()}
            </PopupDecorator>
        );
    }

    private onChange = (value: string) => {
        this.setState({
            text: value
        });
    };

    private getError = (): ErrorItemText | undefined => {
        const { error: serverError, priceLimit, currency } = this.props;
        const { text } = this.state;

        if (serverError) {
            return serverError;
        }

        const clientValidationResult = validatePhraseBid(
            text,
            priceLimit,
            currency
        );

        if (clientValidationResult.errors) {
            return clientValidationResult.errors[0].text;
        }

        return undefined;
    };

    private onSave = () => {
        const { onSave, price } = this.props;
        const { text } = this.state;
        const newPrice = parseNumber(text);

        this.setFormatedText();

        if (newPrice === price) {
            this.props.onCancel();
            return;
        }

        if (!this.getError()) {
            onSave(newPrice);
        }
    };

    private getPopupContent = () => {
        const { text } = this.state;
        const { currency } = this.props;

        return (
            <PhrasePriceEditor
                price={text}
                onChange={this.onChange}
                error={this.getError()}
                currency={currency}
            />
        );
    };

    private cancel = () => {
        const { price, onCancel } = this.props;

        this.setState({
            text: price ? formatNumber(price, 2) : ''
        });

        onCancel();
    };

    private setFormatedText = () => {
        const { text } = this.state;

        const formatedText = formatNumber(parseNumber(text), 2);

        this.setState({
            text: formatedText
        });
    };

    private getFooterContent = () => {
        const { isSaving } = this.props;

        return (
            <>
                <ButtonDecorator
                    theme={'action'}
                    size={'n'}
                    view="default"
                    tone="default"
                    text={i18n('button-submit')}
                    disabled={Boolean(this.getError())}
                    progress={isSaving}
                    onClick={this.onSave}
                />
                <ButtonDecorator
                    theme={'pseudo'}
                    size={'n'}
                    view="default"
                    tone="default"
                    text={i18n('button-cancel')}
                    onClick={this.cancel}
                    cls={Bem.cls('phrase-price-popup-editor', 'button-cancel')}
                />
            </>
        );
    };
}
